import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {RiskConfiguration} from '../../../../shared/interface/risk-configuration';
import {FormArray, FormBuilder, FormGroup, Validators} from '@angular/forms';
import { v4 as uuidv4 } from 'uuid';
import {ReplaySubject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {RiskService} from '../../../../shared/services/risk.service';
import {MatSnackBar} from '@angular/material/snack-bar';
import {HttpErrorResponse} from '@angular/common/http';
import {RiskGroup} from '../../../../shared/interface/risk-group';
import {Risk} from '../../../../shared/interface/risk';
import {RiskRate, TYPE_PARTNERS, TYPE_THRESHOLD} from '../../../../shared/interface/risk-rate';
import {Partner} from '../../../../shared/interface/partner';
import {RiskGroupName} from '../../../../shared/interface/risk-group-name';
import {CommissionTable, CommissionTableListItem} from "../../../../shared/interface/commission-table";
import {RiskCommissionRate} from "../../../../shared/interface/risk-commission-rate";

@Component({
  selector: 'app-risk-group-form',
  templateUrl: './risk-group-form.component.html',
  styleUrls: ['./risk-group-form.component.scss']
})
export class RiskGroupFormComponent implements OnInit, OnDestroy {
  @Input() riskConfiguration: RiskConfiguration;
  @Input() commissionTables: CommissionTableListItem[] = [];
  @Input() visibleCommissionTableUuids: string[] = [];
  @Output() closeModification: EventEmitter<boolean> = new EventEmitter<boolean>();

  readonly TYPE_PARTNERS = TYPE_PARTNERS;
  readonly TYPE_THRESHOLD = TYPE_THRESHOLD;

  formGroupsValid = false;
  formGroups: FormArray;
  processingRequest = false;

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  constructor(
    private fb: FormBuilder,
    private riskService: RiskService,
    private snack: MatSnackBar,
  ) {
    this.formGroups = fb.array([]);
  }

  getVisibleCommissionRateForms(
    riskFormGroup: FormGroup
  ): FormGroup[] {
    return (riskFormGroup.get('commissionRates')as FormArray).controls
      .filter(
        (control: FormGroup) =>
          this.visibleCommissionTableUuids.indexOf(
            control
              .get('commissionTable')
              .value
          ) !== -1
      ) as FormGroup[];
  }

  ngOnInit(): void {
    this.formGroups.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => this.formGroupsValid = this.formGroups.valid);

    this.riskConfiguration.groups.forEach((riskGroup: RiskGroup) => {
      this.addGroup(riskGroup);
    });
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  cancel(): void {
    this.closeModification.emit(false);
  }

  addGroup(initialValue?: RiskGroup): void {
    const formGroup = this.fb.group({
      uuid: this.fb.control(initialValue ? initialValue.uuid : uuidv4()),
      groupNames: this.fb.control(
        initialValue ? initialValue.groupNames.map((riskGroupName: RiskGroupName) => riskGroupName.groupName) : []
      ),
      risks: this.fb.array([], [Validators.minLength(1)])
    });

    initialValue?.risks.forEach((risk: Risk) => {
      this.addRisk((formGroup.get('risks') as FormArray), risk);
    });

    if (!initialValue) {
      this.addRisk((formGroup.get('risks') as FormArray));
    }

    this.formGroups.push(formGroup);
  }

  removeGroup(i: number): void {
    this.formGroups.removeAt(i);
  }

  addRisk(formArray: FormArray, initialValue?: Risk): void {
    const riskFormGroup = this.fb.group({
      uuid: this.fb.control(initialValue ? initialValue.uuid : uuidv4()),
      name: this.fb.control(initialValue ? initialValue.name : '', []),
      riskName: this.fb.control(initialValue ? initialValue.riskName : '', []),
      riders: this.fb.control(initialValue ? initialValue.riders : ''),
      rates: this.fb.control(initialValue ? initialValue.ratesArray : [], []),
      /**
       * LEGACY.
       * Na razie nie usuwamy.
       */
      ratePaid: this.fb.control(initialValue ? initialValue.ratePaid : '', []),
      /**
       * NOWE stawki wg tabel prowizyjnych.
       */
      commissionRates: this.fb.array([]),
      policyType: this.fb.control(initialValue ? (initialValue.policyType?.id ?? '') : ''),
      customRates: this.fb.array([]),
    });

    formArray.push(riskFormGroup);

    this.buildCommissionRates(
      riskFormGroup,
      initialValue
    );

    initialValue?.customRates?.
      sort((a: RiskRate, b: RiskRate) => a.thresholdFrom - b.thresholdFrom)?.
      forEach((riskRate: RiskRate) => {
        this.addRiskRate((riskFormGroup.get('customRates') as FormArray), riskRate);
      });
  }

  addRiskRate(formArray: FormArray, initialValue?: RiskRate, riskRateType?: string): void {
    formArray.push(this.fb.group({
      uuid: this.fb.control(initialValue ? initialValue.uuid : uuidv4()),
      type: this.fb.control(initialValue ? initialValue.type : riskRateType),
      partners: this.fb.control(initialValue ? initialValue.partners.map((partner: Partner) => partner.uuid) : []),
      thresholdFrom: this.fb.control(initialValue ? initialValue.thresholdFrom : ''),
      thresholdTo: this.fb.control(initialValue ? initialValue.thresholdTo : ''),
      ratePaid: this.fb.control(initialValue ? initialValue.ratePaid : '')
    }));
  }

  removeRiskRate(riskFormGroup: FormGroup, riskRateFormGroup: FormGroup): void {
    (riskFormGroup.get('customRates') as FormArray).removeAt(
      (riskFormGroup.get('customRates') as FormArray).controls
        .findIndex((formGroup: FormGroup) =>
          formGroup.get('uuid').value === riskRateFormGroup.get('uuid').value)
    );
  }

  removeRisk(formGroup: FormGroup, i: number): void {
    (formGroup.get('risks') as FormArray).removeAt(i);
  }

  save(): void {
    this.processingRequest = true;

    this.riskService.configure(this.riskConfiguration.uuid, {groups: this.formGroups.value})
      .subscribe({
        next: () => {
          this.processingRequest = false;
          this.snack.open('Konfiguracja została pomyślnie zapisana', '', {duration: 2000});
          this.closeModification.emit(true);
        },
        error: (error: HttpErrorResponse) => {
          this.processingRequest = false;
          this.snack.open('Przy zapisywaniu konfiguracji wystąpił błąd', error.status.toString(), {duration: 2000});
        }
      });
  }

  getRiskRatesForms(riskFormGroup: FormGroup, type: string): FormGroup[] {
    const result: FormGroup[] = [];

    (riskFormGroup.get('customRates') as FormArray).controls.forEach((formGroup: FormGroup) => {
      if (formGroup.get('type').value === type) {
        result.push(formGroup);
      }
    });

    return result;
  }

  private buildCommissionRates(
    riskFormGroup: FormGroup,
    initialValue?: Risk
  ): void {

    const formArray =
      riskFormGroup.get('commissionRates') as FormArray;

    this.commissionTables.forEach(
      (commissionTable: CommissionTableListItem) => {

        const currentRate: RiskCommissionRate =
          initialValue?.commissionRates?.find(
            (rate: RiskCommissionRate) =>
              rate.commissionTableUuid === commissionTable.uuid
          );

        formArray.push(
          this.fb.group({
            uuid: this.fb.control(
              currentRate?.uuid ?? null
            ),
            commissionTable: this.fb.control(
              commissionTable.uuid
            ),
            ratePaid: this.fb.control(
              currentRate?.ratePaid ?? ''
            )
          })
        );
      }
    );
  }
  getCommissionRateForms(
    riskFormGroup: FormGroup
  ): FormGroup[] {
    return (
      riskFormGroup.get('commissionRates') as FormArray
    ).controls as FormGroup[];
  }

  getCommissionTable(
    commissionTableUuid: string
  ): CommissionTableListItem | undefined {

    return this.commissionTables.find(
      (table: CommissionTableListItem) => table.uuid === commissionTableUuid
    );
  }
}
